import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState
} from 'react';
import {
  AppLayoutProps,
  FlashbarProps,
  HelpPanel,
  HelpPanelProps
} from '@amzn/awsui-components-react-v3';
/** data/helpers */
import { addNewMessageV3 } from 'src/data/helpers/utils';

type ProviderProps = {
  children: ReactNode;
};

export type AppLayoutContext = {
  /**
   * @description Breadcrumbs should be defined within this slot in order to benefit
   * from the responsive breadcrumb pattern.
   */
  breadCrumbs: ReactNode;
  /**
   * @description Determines special treatment for the content, suitable for a particular type.
   * Based on the content type, we adjust width of the content slot and the initial state of navigation panel (open or closed).
   * The default values for all content types can be found in this file. If you have a property navigationOpen, minContentWidth or
   * maxContentWidth explicitly set, it will take precedence over the default coming from content type.
   */
  contentType: AppLayoutProps.ContentType;
  /**
   * @description If true, disables outer paddings for the content slot.
   */
  disablePadding: boolean;
  /**
   * @description Displayed on top of the main content in the scrollable area, it occupies the full width and is not affected by the
   * padding that is added to the content slot. Conceived to contain notifications (flash messages).
   */
  notifications: FlashbarProps.MessageDefinition[];
  /**
   * @description Function for appending a new notifiation to the notification slot
   */
  pushNotification: (notification: FlashbarProps.MessageDefinition) => void;
  /** @description dispatcher for `breadCrumbs` */
  setBreadCrumbs: Dispatch<SetStateAction<ReactNode>>;
  /** @description dispatcher for `contentType` */
  setContentType: Dispatch<SetStateAction<AppLayoutProps.ContentType>>;
  /** @description dispatcher for `disablePadding` */
  setDisablePadding: Dispatch<SetStateAction<boolean>>;
  /** @description dispatcher for `tools` */
  setFormattedToolsContent: (toolsContent: HelpPanelProps) => void;
  /** @description dispatcher for `toolsOpen` */
  setToolsOpen: Dispatch<SetStateAction<boolean>>;
  /** @description Tools drawer. */
  tools: ReactNode;
  /** @description State of the tools drawer. */
  toolsOpen: boolean;
}

export const Context = /*#__PURE__*/ createContext<AppLayoutContext>({} as AppLayoutContext);

if (process.env.NODE_ENV !== 'production') {
  Context.displayName = 'AppLayout'
}

/**
 * @author jonaoroz
 * @param {ReactNode} children
 */
const AppLayoutProvider = ({ children }: ProviderProps): JSX.Element => {
  // Properties
  const [contentType, setContentType] = useState<AppLayoutProps.ContentType>('default');
  const [disablePadding, setDisablePadding] = useState<boolean>(false);
  const [toolsOpen, setToolsOpen] = useState<boolean>(false);
  // Slots
  const [breadCrumbs, setBreadCrumbs] = useState<ReactNode>(null);
  const [notifications, setNotification] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [tools, setTools] = useState<ReactNode>(null);
  /**
   * @description Handler for setting the tools property with the HelpPanel component.
   * @param {HelpPanelProps} toolsContent
   * @returns {void}
   */
  const setFormattedToolsContent = (toolsContent: HelpPanelProps): void => {
    setTools(<HelpPanel {...toolsContent} />)
  };
  /**
   * @description Handler for adding a notification object to the array
   * @param {FlashbarProps.MessageDefinition} notification
   * @returns {void}
   */
  const pushNotification = (notification: FlashbarProps.MessageDefinition): void => {
    addNewMessageV3(setNotification, notification);
  };

  return (
    <Context.Provider value={{
      // values
      breadCrumbs,
      contentType,
      disablePadding,
      notifications,
      tools,
      toolsOpen,
      // setters
      pushNotification,
      setBreadCrumbs,
      setContentType,
      setDisablePadding,
      setFormattedToolsContent,
      setToolsOpen
    }}>
      {children}
    </Context.Provider>
  );
};

export const useAppLayoutProvider = () => useContext(Context);
export default AppLayoutProvider;
